import classNames from "classnames";
import React, { Children, FC, PropsWithChildren, ReactElement, useEffect, useId } from "react";
import { NavLink, To, useLocation } from "react-router-dom";
import useLocalStorage from "../../hooks/useLocalStorage";
import { Glyph } from "../../types";
import SvgArrowDropDown from "../icons/ArrowDropDown";
import SvgOpenInNew from "../icons/OpenInNew";
import Icon from "./Icon";
import styles from "./Nav.module.css";

const Nav = ({ children }: PropsWithChildren) => {
  return (
    <nav>
      <NavList>{children}</NavList>
    </nav>
  );
};

type NavListProps = PropsWithChildren<{ id?: string; "aria-labelledby"?: string }>;

const NavList = ({ children, ...rest }: NavListProps) => (
  <ul className={styles.ul} role="list" {...rest}>
    {children}
  </ul>
);

type NavGroupProps = PropsWithChildren<{ title: string }>;

const NavGroup = ({ title, children }: NavGroupProps) => {
  const titleId = useId();

  return (
    <li className={styles.group}>
      <h3 id={titleId} className={styles.title}>
        {title}
      </h3>
      {!!children && <NavList aria-labelledby={titleId}>{children}</NavList>}
    </li>
  );
};

interface NavSubItemProps {
  text: string;
  icon?: Glyph;
  to: To;
  target?: React.HTMLAttributeAnchorTarget;
  onClick?: () => void;
  activeMatch?: (pathname: string) => boolean;
}

type NavItemChildren = ReactElement<NavSubItemProps> | ReactElement<NavSubItemProps>[];

type NavItemProps =
  | (NavSubItemProps & { children?: NavItemChildren; storageKey?: string })
  | { text: string; icon?: Glyph; to?: undefined; children: NavItemChildren; storageKey?: string };

const NavItem: FC<NavItemProps> = (props) => {
  const { text, icon, to, children } = props;
  const target = "target" in props ? (props.target ?? "_self") : "_self";
  const onClick = "onClick" in props ? props.onClick : undefined;
  const activeMatch = "activeMatch" in props ? props.activeMatch : undefined;
  const storageKey = "storageKey" in props ? props.storageKey : undefined;
  const { pathname } = useLocation();
  const submenuId = useId();
  const hasChildren = Children.count(children) > 0;
  const basePath = to ? (typeof to === "string" ? to : (to.pathname ?? "")) : "";
  const defaultMatch = basePath !== "" && (pathname === basePath || pathname.startsWith(`${basePath}/`));
  const isLinkActive = activeMatch ? activeMatch(pathname) : defaultMatch;
  // Auto-expand ignores a custom activeMatch override so the submenu still opens when a child route is active,
  // even if the parent link itself is scoped to not highlight for those same routes.
  const isDescendantActive = hasChildren && defaultMatch;
  const [isOpen, setIsOpen] = useLocalStorage<boolean>(
    hasChildren && storageKey ? `nav.open.${storageKey}` : null,
    isDescendantActive,
  );

  useEffect(() => {
    if (isDescendantActive) setIsOpen(true);
  }, [isDescendantActive]);

  return (
    <li>
      <div className={styles.itemRow}>
        {to ? (
          <NavLink
            to={to}
            target={target}
            onClick={onClick}
            className={classNames(styles.link, {
              [styles.linkActive]: isLinkActive,
            })}
          >
            {icon && <Icon glyph={icon} className={styles.linkIcon} />}
            {text}
            {target === "_blank" && <Icon glyph={SvgOpenInNew} className={styles.openInNew} />}
          </NavLink>
        ) : (
          <button
            type="button"
            className={styles.link}
            aria-expanded={isOpen}
            aria-controls={submenuId}
            onClick={() => setIsOpen((open) => !open)}
          >
            {icon && <Icon glyph={icon} className={styles.linkIcon} />}
            {text}
            <Icon
              glyph={SvgArrowDropDown}
              className={classNames(styles.toggleIcon, styles.toggleIconInline, {
                [styles.toggleIconOpen]: isOpen,
              })}
            />
          </button>
        )}
        {hasChildren && to && (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={isOpen}
            aria-controls={submenuId}
            aria-label={text}
            onClick={() => setIsOpen((open) => !open)}
          >
            <Icon
              glyph={SvgArrowDropDown}
              className={classNames(styles.toggleIcon, { [styles.toggleIconOpen]: isOpen })}
            />
          </button>
        )}
      </div>
      {hasChildren && (
        <div className={classNames(styles.submenu, { [styles.submenuOpen]: isOpen })} inert={!isOpen}>
          <NavList id={submenuId}>
            {Children.map(children!, (child) =>
              React.cloneElement(child, {
                to: to ? `${to}/${child.props.to}` : child.props.to,
              }),
            )}
          </NavList>
        </div>
      )}
    </li>
  );
};

Nav.Group = NavGroup;
Nav.Item = NavItem;

export default Nav;
